import { ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, AppWindow, Calendar, Settings, LogOut, Loader2, Sparkles, PanelsTopLeft } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const items = [
  {
    title: "Dashboard",
    description: "Overview and activity",
    url: "/control-panel",
    icon: LayoutDashboard,
    end: true,
  },
  {
    title: "Apps",
    description: "API keys and verification",
    url: "/control-panel/apps",
    icon: AppWindow,
    end: false,
  },
  {
    title: "Meetings",
    description: "Schedule and launch rooms",
    url: "/control-panel/meetings",
    icon: Calendar,
    end: false,
  },
  {
    title: "Settings",
    description: "Account preferences",
    url: "/control-panel/settings",
    icon: Settings,
    end: false,
  },
];

const sectionTitles: Record<string, string> = {
  "/control-panel": "Workspace dashboard",
  "/control-panel/apps": "App management",
  "/control-panel/meetings": "Meeting operations",
  "/control-panel/settings": "Workspace settings",
};

export default function ControlPanelLayout({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    navigate("/auth");
    return null;
  }

  const logout = async () => {
    await supabase.auth.signOut();
    toast.success("Signed out");
    navigate("/");
  };

  const currentSection = sectionTitles[location.pathname] || "Control panel";

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-[radial-gradient(circle_at_top_left,hsl(var(--primary)/0.08),transparent_30%),linear-gradient(180deg,hsl(var(--background)),hsl(var(--muted)/0.22))]">
        <Sidebar collapsible="icon" className="border-r border-sidebar-border/70">
          <SidebarHeader className="px-3 py-4">
            <NavLink to="/control-panel" className="flex items-start gap-3 rounded-2xl border border-sidebar-border/70 bg-sidebar-accent/30 p-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sidebar-primary text-sidebar-primary-foreground shadow-sm">
                <PanelsTopLeft className="h-5 w-5" />
              </div>
              <div className="group-data-[collapsible=icon]:hidden">
                <p className="font-semibold leading-none">MeetHub Console</p>
                <p className="mt-1 text-xs text-sidebar-foreground/70">Apps, meetings, and workspace tools</p>
              </div>
            </NavLink>
          </SidebarHeader>
          <SidebarContent>
            <SidebarGroup>
              <SidebarGroupLabel>Workspace</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <NavLink
                          to={item.url}
                          end={item.end}
                          className={({ isActive }) =>
                            `rounded-xl border px-2 py-2 transition-colors ${
                              isActive
                                ? "border-sidebar-primary/30 bg-sidebar-primary/10 text-sidebar-foreground"
                                : "border-transparent hover:bg-sidebar-accent/50"
                            }`
                          }
                        >
                          <item.icon className="h-4 w-4" />
                          <span className="group-data-[collapsible=icon]:hidden">
                            <span className="block text-sm font-medium">{item.title}</span>
                            <span className="mt-0.5 block text-xs text-sidebar-foreground/70">{item.description}</span>
                          </span>
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>
          <SidebarFooter className="p-3">
            <div className="rounded-2xl border border-sidebar-border/70 bg-sidebar-accent/20 p-3 group-data-[collapsible=icon]:hidden">
              <p className="text-sm font-medium">Signed in</p>
              <p className="mt-1 truncate text-xs text-sidebar-foreground/70">{user.email}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={logout} className="mt-2 justify-start rounded-xl">
              <LogOut className="h-4 w-4 mr-2" />
              <span className="group-data-[collapsible=icon]:hidden">Sign out</span>
            </Button>
          </SidebarFooter>
        </Sidebar>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="sticky top-0 z-10 border-b border-border/60 bg-background/80 backdrop-blur-xl">
            <div className="mx-auto flex h-16 w-full max-w-6xl items-center gap-4 px-4 md:px-6">
              <SidebarTrigger />
              <div className="min-w-0 flex-1">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Control panel</p>
                <div className="flex items-center gap-2">
                  <h1 className="truncate text-sm font-semibold md:text-base">{currentSection}</h1>
                  <span className="hidden rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary md:inline-flex">
                    <Sparkles className="mr-1 h-3 w-3" />
                    Internal workspace
                  </span>
                </div>
              </div>
              <div className="hidden text-right md:block">
                <p className="text-xs text-muted-foreground">Signed in as</p>
                <p className="max-w-52 truncate text-sm font-medium">{user.email}</p>
              </div>
            </div>
          </header>

          <main className="flex-1">
            <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-6 md:py-8">{children}</div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
